import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import Box from '@/main/components/shared/box';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import RenderTooltipMessage from '@/main/components/shared/renderTooltipMessage/renderTooltipMessage';
import { getTranslateByKey } from '@/main/services/utils';
import { TemplateEmailNotificationVariableResponse } from '@/main/models/response/templateEmailNotificationResponse';

import * as S from './styles';


interface IVariableTemplateEmailProps {
  variables: TemplateEmailNotificationVariableResponse[];
  activeTitleContent: string;
  activeBodyContent: string;
}

function VariableTemplateEmail({
  variables,
  activeTitleContent,
  activeBodyContent
}: IVariableTemplateEmailProps) {
  const subjectVariables = variables.filter(v => v.isSubject);
  const bodyVariables = variables.filter(v => !v.isSubject);

  function isSubjectVariableUsed(variable: string): boolean {
    return activeTitleContent.includes(variable);
  }

  function isBodyVariableUsed(variable: string): boolean {
    return activeBodyContent.includes(variable);
  }

  async function handleCopy(variable: string) {
    try {
      await navigator.clipboard.writeText(variable);
    } catch {
      // Clipboard not available (e.g. non-secure context) — silently ignore
    }
  }

  function renderChip(v: TemplateEmailNotificationVariableResponse, used: boolean) {
    const chip = (
      <S.VariableChip key={v.variable} $used={used}>
        <S.VariableName>{v.variable}</S.VariableName>
        <S.CopyButton
          id={`id-copy-variable-${v.variable}`}
          title={getTranslateByKey('information.copy')}
          onClick={() => handleCopy(v.variable)}>
          <ContentCopyIcon fontSize="small" />
        </S.CopyButton>
      </S.VariableChip>
    );

    if (!used) {
      return (
        <RenderTooltipMessage
          key={v.variable}
          tooltipMessage={getTranslateByKey('information.variableMandatory')}
          placement="top"
        >
          {chip}
        </RenderTooltipMessage>
      );
    }

    return chip;
  }

  return (
    <S.VariableList>
      <S.SectionTitle>{getTranslateByKey('information.availableVariables')}</S.SectionTitle>
      <S.GroupDivider />
      <S.GroupLabel>{getTranslateByKey('information.title')}</S.GroupLabel>
      <S.GroupDivider />
      {subjectVariables.length === 0
        ? <ResultNotFoundComponent />
        : subjectVariables.map(v => renderChip(v, isSubjectVariableUsed(v.variable)))
      }
      <S.GroupLabel>{getTranslateByKey('information.body')}</S.GroupLabel>
      <S.GroupDivider />
      {bodyVariables.length === 0
        ? <ResultNotFoundComponent />
        : bodyVariables.map(v => renderChip(v, isBodyVariableUsed(v.variable)))
      }
    </S.VariableList>
  );
}

export default VariableTemplateEmail;
